if (!customElements.get('postal-code-form')) {
  customElements.define(
    'postal-code-form',
    class PostalCodeForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.input = this.querySelector('.js-postal-code-form__input');
        this.messages = this.querySelector('.js-postal-code-form__messages');
        this.reset = this.querySelector('.js-postal-code-form__reset');
        this.results = this.querySelector('.js-postal-code-form__results');
        this.loading = this.querySelector('.js-postal-code-form__results__loading');
        this.content = this.querySelector('.js-postal-code-form__results__content');
        this.deliveryTimeResult = this.querySelector('.js-postal-code-form__result--delivery-time');
        this.deliveryTimeResultValue = this.querySelector('.js-postal-code-form__result--delivery-time__value');
        this.deliveryCostResult = this.querySelector('.js-postal-code-form__result--delivery-cost');
        this.deliveryCostResultValue = this.querySelector('.js-postal-code-form__result--delivery-cost__value');
        this.freeShippingResult = this.querySelector('.js-postal-code-form__result--free-shipping');

        this.inputChangeHandler = FoxTheme.utils.debounce(this.handleInputChange.bind(this), 500);
        this.resetHandler = this.handleReset.bind(this);

        this.mapEvents();
        this.initDeferred();
      }

      disconnectedCallback() {
        this.input.removeEventListener('input', this.inputChangeHandler);
        this.reset.removeEventListener('click', this.resetHandler);

        if (this.modal && this.openHandler) {
          this.modal.removeEventListener('modal:handleAfterShow', this.openHandler);
        }

        // Laufenden Request abbrechen, damit keine späte Antwort detached UI/Storage anfasst.
        if (this.controller) {
          this.controller.abort();
          this.controller = null;
          this.toggleLoadingSpinner(false);
        }
      }

      initDeferred() {
        // Restore/Fetch erst beim Öffnen des Off-Canvas, nicht schon beim Seitenladen.
        this.modal = this.closest('basic-modal');

        if (!this.modal) {
          this.restoreFromLocalStorage();

          return;
        }

        this.openHandler = () => {
          if (this.initialized) {
            return;
          }

          this.initialized = true;
          this.restoreFromLocalStorage();
        };

        this.modal.addEventListener('modal:handleAfterShow', this.openHandler);
      }

      mapEvents() {
        this.input.addEventListener('input', this.inputChangeHandler, false);
        this.reset.addEventListener('click', this.resetHandler);
      }

      handleInputChange(event) {
        const target = event.target;
        const value = target.value.trim();

        // Bereits angezeigte PLZ -> kein erneuter Webhook-Call.
        if (value && value === this.currentPlz && !this.content.classList.contains('hidden')) {
            return;
        }

        this.hideDeliveryInfo();
        this.toggleError(false);

        if (!target.validity.valid) {
            this.toggleError(true);

            return;
        }

        if (value) {
            this.fetchDeliveryInfo(value);
        }
      }

      handleReset(event) {
        event.preventDefault();

        this.hideDeliveryInfo();
        this.toggleError(false);

        this.input.value = '';
        this.input.setAttribute('aria-invalid', 'false');
        this.input.focus();
        FoxTheme.utils.setStorage(FoxTheme.storageKeys.plzCheck, {}, 1);
      }

      toggleLoadingSpinner(show) {
        this.results.setAttribute('aria-busy', show ? 'true' : 'false');
        this.loading.classList.toggle('hidden', !show);
      }

      formatDeliveryTime(minDays, maxDays) {
        // Fehlende Werte explizit ablehnen (Number(null)/Number('') würde sonst 0 ergeben).
        if (minDays == null || minDays === '' || maxDays == null || maxDays === '') {
          return '';
        }

        const min = Number(minDays);
        const max = Number(maxDays);

        // Ungültige oder invertierte Range -> nichts anzeigen.
        if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= 0 || min > max) {
          return '';
        }

        // Minimum unter 7 Tagen -> in Tagen anzeigen (Singular/Plural beachten).
        if (min < 7) {
          if (min === max) {
            return max === 1 ? '1 Tag' : `${max} Tage`;
          }

          return `${min}–${max} Tage`;
        }

        // Minimum ab 7 Tagen -> in Wochen (min kaufmännisch gerundet, max aufgerundet).
        const minWeeks = Math.round(min / 7);
        const maxWeeks = Math.ceil(max / 7);

        if (minWeeks === maxWeeks) {
          return minWeeks === 1 ? '1 Woche' : `${minWeeks} Wochen`;
        }

        return `${minWeeks}–${maxWeeks} Wochen`;
      }

      showDeliveryInfo(minDeliveryDate, maxDeliveryDate, totalPrice, postalCode, minDays, maxDays) {
        const deliveryTimeframe = this.formatDeliveryTime(minDays, maxDays)
           || FoxTheme.utils.formatTimeframe(new Date(minDeliveryDate), new Date(maxDeliveryDate));
        const deliveryCost = FoxTheme.Currency.formatMoney(totalPrice, FoxTheme.settings.moneyFormat);

        this.content.classList.remove('hidden');

        this.deliveryTimeResult.classList.remove('hidden');
        this.deliveryTimeResultValue.textContent = deliveryTimeframe;

        if (Number(totalPrice) > 0) {
            this.deliveryCostResult.classList.remove('hidden');
            this.deliveryCostResultValue.textContent = deliveryCost;
        } else {
            this.freeShippingResult.classList.remove('hidden');
        }

        const subtitle = document.querySelector('.js-postal-code-form__trigger-subtitle');

        if (subtitle) {
            subtitle.textContent = subtitle.dataset.text.replace('%deliveryTime%', deliveryTimeframe).replace('%postalCode%', postalCode);
        }
      }

      hideDeliveryInfo() {
        this.content.classList.add('hidden');
        this.deliveryTimeResult.classList.add('hidden');
        this.deliveryCostResult.classList.add('hidden');
        this.freeShippingResult.classList.add('hidden');

        const subtitle = document.querySelector('.js-postal-code-form__trigger-subtitle');

        if (subtitle) {
            subtitle.textContent = subtitle.dataset.textDefault;
        }
      }

      toggleError(show) {
        this.input.setAttribute('aria-invalid', show);
        this.input.classList.toggle('invalid', show);
        this.messages.classList.toggle('hidden', !show);
      }

      restoreFromLocalStorage() {
        const result = FoxTheme.utils.getStorage(FoxTheme.storageKeys.plzCheck);

        if (!this.sku || !result?.plz) {
          return;
        }

        const skus = Array.isArray(result.skus) ? result.skus : [];

        // Nur wenn für DIESES Produkt ein Ergebnis gespeichert ist: PLZ + Ergebnis anzeigen.
        // Sonst nichts vorausfüllen und nicht fetchen.
        if (!skus.includes(this.sku)) {
          return;
        }

        this.input.value = result.plz;
        this.currentPlz = result.plz;
        this.showDeliveryInfo(
          result.min_delivery_date,
          result.max_delivery_date,
          result.total_price,
          result.plz,
          result.min_days,
          result.max_days
        );
      }

      async fetchDeliveryInfo(postalCode) {
        this.toggleLoadingSpinner(true);

        // Laufenden Request abbrechen, damit eine späte Antwort keine neuere überschreibt.
        if (this.controller) {
            this.controller.abort();
        }
        this.controller = new AbortController();
        const controller = this.controller;

        const body = {
            postal_code: postalCode,
            delivery_time_min: this.deliveryTimeMin,
            delivery_time_max: this.deliveryTimeMax,
            delivery_cost: this.deliveryCost,
            shipping_method: this.shippingMethod,
            sku: this.sku,
        }

        await fetch('https://hardeck.app.n8n.cloud/webhook/plz-check', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            signal: controller.signal,
        })
            .then((response) => response.json())
            .then((data) => {
                // Webhook liefert ein Objekt: { rates: [ {...} ] }.
                const rate = data?.rates?.[0] ?? null;

                if (!rate) {
                    this.toggleError(true);

                    return;
                }

                const { min_delivery_date, max_delivery_date, total_price, min_days, max_days } = rate;

                this.currentPlz = postalCode;
                this.showDeliveryInfo(min_delivery_date, max_delivery_date, total_price, postalCode, min_days, max_days);

                FoxTheme.utils.setStorage(FoxTheme.storageKeys.plzCheck, {
                    skus: [this.sku],
                    plz: postalCode,
                    min_delivery_date,
                    max_delivery_date,
                    total_price,
                    min_days,
                    max_days,
                }, 1);
            }).catch((error) => {
                if (error.name === 'AbortError') {
                    return;
                }

                this.toggleError(true);
            })
            .finally(() => {
                // Nur der zuletzt gestartete Request beendet den Spinner.
                if (this.controller === controller) {
                    this.toggleLoadingSpinner(false);
                }
            });
      }

      get deliveryTimeMin() {
        return this.dataset.deliveryTimeMin;
      }

      get deliveryTimeMax() {
        return this.dataset.deliveryTimeMax;
      }

      get deliveryCost() {
        return this.dataset.deliveryCost;
      }

      get shippingMethod() {
        return this.dataset.shippingMethod;
      }

      get sku() {
        return this.dataset.sku;
      }
    }
  );
}
